import { useState } from 'react';
import { Title, useNotify } from 'react-admin';
import { useQuery, useMutation } from '@apollo/client';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Skeleton from '@mui/material/Skeleton';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import { useNavigate } from 'react-router-dom';

import { GET_PLANS, CREATE_PLAN, UPDATE_PLAN, UPDATE_PLAN_STATUS } from './subscription.queries';
import {
  AdminPlan,
  AdminPlanStatus,
  AdminPlanCategory,
  PLAN_STATUS_LABELS,
  PLAN_STATUS_COLORS,
  CATEGORY_LABELS,
  formatPrice,
} from './subscription.types';

// ============================================================================
// PLAN CARD COMPONENT
// ============================================================================

interface PlanCardProps {
  plan: AdminPlan;
  onEdit: (plan: AdminPlan) => void;
  onStatusChange: (planId: string, status: AdminPlanStatus) => void;
}

function PlanCard({ plan, onEdit, onStatusChange }: PlanCardProps) {
  const navigate = useNavigate();

  const statusColor = PLAN_STATUS_COLORS[plan.status];

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4,
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          backgroundColor: plan.colorPrimary || '#6366F1',
        },
      }}
    >
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="h6" fontWeight={600}>
                {plan.name}
              </Typography>
              {plan.isPopular && (
                <Chip
                  icon={<StarIcon sx={{ fontSize: 14 }} />}
                  label="Populaire"
                  size="small"
                  color="warning"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              {plan.code}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Voir détails">
              <IconButton size="small" onClick={() => navigate(`/plans/${plan.id}`)}>
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Modifier">
              <IconButton size="small" onClick={() => onEdit(plan)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Status & Category */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Chip
            label={PLAN_STATUS_LABELS[plan.status]}
            size="small"
            sx={{
              backgroundColor: `${statusColor}20`,
              color: statusColor,
              fontWeight: 500,
            }}
          />
          <Chip
            label={CATEGORY_LABELS[plan.category]}
            size="small"
            variant="outlined"
          />
        </Box>

        {/* Description */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 2,
            minHeight: 40,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {plan.description || 'Aucune description'}
        </Typography>

        {/* Pricing */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5" fontWeight={700} color="primary">
            {formatPrice(plan.monthlyPrice)}
            <Typography component="span" variant="body2" color="text.secondary">
              /mois
            </Typography>
          </Typography>
          {plan.yearlyPrice && (
            <Typography variant="body2" color="text.secondary">
              ou {formatPrice(plan.yearlyPrice)}/an
              {plan.yearlyDiscount && (
                <Chip
                  label={`-${plan.yearlyDiscount}%`}
                  size="small"
                  color="success"
                  sx={{ ml: 1, height: 18, fontSize: '0.65rem' }}
                />
              )}
            </Typography>
          )}
        </Box>

        {/* Highlights */}
        {plan.highlights && plan.highlights.length > 0 && (
          <Box sx={{ mb: 2 }}>
            {plan.highlights.slice(0, 3).map((highlight, idx) => (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />
                <Typography variant="body2">{highlight}</Typography>
              </Box>
            ))}
            {plan.highlights.length > 3 && (
              <Typography variant="caption" color="text.secondary">
                +{plan.highlights.length - 3} autres fonctionnalités
              </Typography>
            )}
          </Box>
        )}

        {/* Trial Days */}
        {plan.trialDays > 0 && (
          <Chip
            label={`${plan.trialDays} jours d'essai gratuit`}
            size="small"
            variant="outlined"
            color="info"
            sx={{ alignSelf: 'flex-start', mb: 2 }}
          />
        )}

        {/* Spacer */}
        <Box sx={{ flex: 1 }} />

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          {plan.status === AdminPlanStatus.DRAFT && (
            <Button
              size="small"
              variant="contained"
              onClick={() => onStatusChange(plan.id, AdminPlanStatus.REVIEW)}
            >
              Soumettre à révision
            </Button>
          )}
          {plan.status === AdminPlanStatus.REVIEW && (
            <>
              <Button
                size="small"
                variant="contained"
                color="success"
                onClick={() => onStatusChange(plan.id, AdminPlanStatus.ACTIVE)}
              >
                Activer
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => onStatusChange(plan.id, AdminPlanStatus.DRAFT)}
              >
                Retour brouillon
              </Button>
            </>
          )}
          {plan.status === AdminPlanStatus.ACTIVE && (
            <Button
              size="small"
              variant="outlined"
              color="warning"
              onClick={() => onStatusChange(plan.id, AdminPlanStatus.PAUSED)}
            >
              Mettre en pause
            </Button>
          )}
          {plan.status === AdminPlanStatus.PAUSED && (
            <Button
              size="small"
              variant="contained"
              onClick={() => onStatusChange(plan.id, AdminPlanStatus.ACTIVE)}
            >
              Réactiver
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// CREATE PLAN DIALOG
// ============================================================================

interface CreatePlanDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

function CreatePlanDialog({ open, onClose, onCreated }: CreatePlanDialogProps) {
  const notify = useNotify();
  const [createPlan, { loading }] = useMutation(CREATE_PLAN);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    category: AdminPlanCategory.B2C,
    monthlyPrice: 0,
    yearlyPrice: undefined as number | undefined,
    yearlyDiscount: undefined as number | undefined,
    trialDays: 14,
    isPopular: false,
    colorPrimary: '#10B981',
  });

  const handleSubmit = async () => {
    try {
      await createPlan({
        variables: {
          ...formData,
          yearlyPrice: formData.yearlyPrice || null,
          yearlyDiscount: formData.yearlyDiscount || null,
        },
      });
      notify('Forfait créé avec succès', { type: 'success' });
      onCreated();
      onClose();
      setFormData({
        code: '',
        name: '',
        description: '',
        category: AdminPlanCategory.B2C,
        monthlyPrice: 0,
        yearlyPrice: undefined,
        yearlyDiscount: undefined,
        trialDays: 14,
        isPopular: false,
        colorPrimary: '#10B981',
      });
    } catch {
      notify('Erreur lors de la création du forfait', { type: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Créer un nouveau forfait</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Code"
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
            fullWidth
            required
            helperText="Identifiant unique (ex: PRO, BUSINESS)"
          />
          <TextField
            label="Nom"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            fullWidth
            required
          />
          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            fullWidth
            multiline
            rows={2}
          />
          <FormControl fullWidth>
            <InputLabel>Catégorie</InputLabel>
            <Select
              value={formData.category}
              label="Catégorie"
              onChange={(e) => setFormData({ ...formData, category: e.target.value as AdminPlanCategory })}
            >
              {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Divider />
          <Typography variant="subtitle2">Tarification</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="Prix mensuel (FCFA)"
                type="number"
                value={formData.monthlyPrice}
                onChange={(e) => setFormData({ ...formData, monthlyPrice: Number(e.target.value) })}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Prix annuel (FCFA)"
                type="number"
                value={formData.yearlyPrice || ''}
                onChange={(e) => setFormData({ ...formData, yearlyPrice: Number(e.target.value) || undefined })}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Réduction annuelle (%)"
                type="number"
                value={formData.yearlyDiscount || ''}
                onChange={(e) => setFormData({ ...formData, yearlyDiscount: Number(e.target.value) || undefined })}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Jours d'essai"
                type="number"
                value={formData.trialDays}
                onChange={(e) => setFormData({ ...formData, trialDays: Number(e.target.value) })}
                fullWidth
              />
            </Grid>
          </Grid>
          <Divider />
          <Typography variant="subtitle2">Options</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isPopular}
                  onChange={(e) => setFormData({ ...formData, isPopular: e.target.checked })}
                />
              }
              label="Forfait populaire"
            />
            <TextField
              label="Couleur"
              type="color"
              value={formData.colorPrimary}
              onChange={(e) => setFormData({ ...formData, colorPrimary: e.target.value })}
              sx={{ width: 100 }}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading || !formData.code || !formData.name}>
          {loading ? 'Création...' : 'Créer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ============================================================================
// EDIT PLAN DIALOG
// ============================================================================

interface EditPlanDialogProps {
  open: boolean;
  plan: AdminPlan | null;
  onClose: () => void;
  onUpdated: () => void;
}

function EditPlanDialog({ open, plan, onClose, onUpdated }: EditPlanDialogProps) {
  const notify = useNotify();
  const [updatePlan, { loading }] = useMutation(UPDATE_PLAN);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    monthlyPrice: 0,
    yearlyPrice: undefined as number | undefined,
    yearlyDiscount: undefined as number | undefined,
    trialDays: 14,
    isPopular: false,
    colorPrimary: '#10B981',
    highlights: [] as string[],
    maxUsers: undefined as number | undefined,
  });
  const [newHighlight, setNewHighlight] = useState('');

  // Populate form when plan changes
  useState(() => {
    if (plan) {
      setFormData({
        name: plan.name,
        description: plan.description || '',
        monthlyPrice: plan.monthlyPrice,
        yearlyPrice: plan.yearlyPrice || undefined,
        yearlyDiscount: plan.yearlyDiscount || undefined,
        trialDays: plan.trialDays,
        isPopular: plan.isPopular,
        colorPrimary: plan.colorPrimary || '#10B981',
        highlights: plan.highlights || [],
        maxUsers: plan.maxUsers || undefined,
      });
    }
  });

  // Update form when plan prop changes
  const handleOpen = () => {
    if (plan) {
      setFormData({
        name: plan.name,
        description: plan.description || '',
        monthlyPrice: plan.monthlyPrice,
        yearlyPrice: plan.yearlyPrice || undefined,
        yearlyDiscount: plan.yearlyDiscount || undefined,
        trialDays: plan.trialDays,
        isPopular: plan.isPopular,
        colorPrimary: plan.colorPrimary || '#10B981',
        highlights: plan.highlights || [],
        maxUsers: plan.maxUsers || undefined,
      });
    }
  };

  // Call handleOpen when dialog opens
  if (open && plan && formData.name !== plan.name) {
    handleOpen();
  }

  const handleSubmit = async () => {
    if (!plan) return;
    try {
      await updatePlan({
        variables: {
          id: plan.id,
          name: formData.name,
          description: formData.description || null,
          monthlyPrice: formData.monthlyPrice,
          yearlyPrice: formData.yearlyPrice || null,
          yearlyDiscount: formData.yearlyDiscount || null,
          trialDays: formData.trialDays,
          isPopular: formData.isPopular,
          colorPrimary: formData.colorPrimary,
          highlights: formData.highlights.length > 0 ? formData.highlights : null,
          maxUsers: formData.maxUsers || null,
        },
      });
      notify('Forfait mis à jour avec succès', { type: 'success' });
      onUpdated();
      onClose();
    } catch {
      notify('Erreur lors de la mise à jour du forfait', { type: 'error' });
    }
  };

  const addHighlight = () => {
    if (newHighlight.trim()) {
      setFormData({ ...formData, highlights: [...formData.highlights, newHighlight.trim()] });
      setNewHighlight('');
    }
  };

  const removeHighlight = (index: number) => {
    setFormData({
      ...formData,
      highlights: formData.highlights.filter((_, i) => i !== index),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Modifier le forfait: {plan?.name}
        <Typography variant="caption" display="block" color="text.secondary">
          Code: {plan?.code}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Nom"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            fullWidth
            required
          />
          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            fullWidth
            multiline
            rows={3}
          />

          <Divider />
          <Typography variant="subtitle2">Tarification</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <TextField
                label="Prix mensuel (FCFA)"
                type="number"
                value={formData.monthlyPrice}
                onChange={(e) => setFormData({ ...formData, monthlyPrice: Number(e.target.value) })}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="Prix annuel (FCFA)"
                type="number"
                value={formData.yearlyPrice || ''}
                onChange={(e) => setFormData({ ...formData, yearlyPrice: Number(e.target.value) || undefined })}
                fullWidth
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="Réduction annuelle (%)"
                type="number"
                value={formData.yearlyDiscount || ''}
                onChange={(e) => setFormData({ ...formData, yearlyDiscount: Number(e.target.value) || undefined })}
                fullWidth
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="Jours d'essai"
                type="number"
                value={formData.trialDays}
                onChange={(e) => setFormData({ ...formData, trialDays: Number(e.target.value) })}
                fullWidth
              />
            </Grid>
          </Grid>

          <Divider />
          <Typography variant="subtitle2">Points forts (Highlights)</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              label="Ajouter un point fort"
              value={newHighlight}
              onChange={(e) => setNewHighlight(e.target.value)}
              fullWidth
              size="small"
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addHighlight())}
            />
            <Button variant="outlined" onClick={addHighlight}>
              Ajouter
            </Button>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {formData.highlights.map((highlight, index) => (
              <Chip
                key={index}
                label={highlight}
                onDelete={() => removeHighlight(index)}
                color="primary"
                variant="outlined"
              />
            ))}
          </Box>

          <Divider />
          <Typography variant="subtitle2">Options</Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isPopular}
                    onChange={(e) => setFormData({ ...formData, isPopular: e.target.checked })}
                  />
                }
                label="Forfait populaire"
              />
            </Grid>
            <Grid item xs={6} md={4}>
              <TextField
                label="Couleur primaire"
                type="color"
                value={formData.colorPrimary}
                onChange={(e) => setFormData({ ...formData, colorPrimary: e.target.value })}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: 1,
                        backgroundColor: formData.colorPrimary,
                        mr: 1,
                      }}
                    />
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6} md={4}>
              <TextField
                label="Max utilisateurs"
                type="number"
                value={formData.maxUsers || ''}
                onChange={(e) => setFormData({ ...formData, maxUsers: Number(e.target.value) || undefined })}
                fullWidth
                helperText="Laisser vide pour illimité"
              />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading || !formData.name}>
          {loading ? 'Mise à jour...' : 'Enregistrer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function PlanList() {
  const notify = useNotify();
  const [tabValue, setTabValue] = useState(0);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<AdminPlan | null>(null);

  const { data, loading, error, refetch } = useQuery(GET_PLANS, {
    variables: tabValue === 0 ? {} : { status: tabValue === 1 ? 'ACTIVE' : tabValue === 2 ? 'DRAFT' : 'ARCHIVED' },
  });

  const [updateStatus] = useMutation(UPDATE_PLAN_STATUS);

  const handleStatusChange = async (planId: string, status: AdminPlanStatus) => {
    try {
      await updateStatus({ variables: { id: planId, status } });
      notify('Statut mis à jour', { type: 'success' });
      refetch();
    } catch {
      notify('Erreur lors de la mise à jour du statut', { type: 'error' });
    }
  };

  const handleEdit = (plan: AdminPlan) => {
    setSelectedPlan(plan);
    setEditDialogOpen(true);
  };

  const plans: AdminPlan[] = data?.adminPlans || [];

  // Filter by tab
  const filteredPlans = tabValue === 0
    ? plans
    : tabValue === 1
    ? plans.filter((p) => p.status === AdminPlanStatus.ACTIVE)
    : tabValue === 2
    ? plans.filter((p) => p.status === AdminPlanStatus.DRAFT || p.status === AdminPlanStatus.REVIEW)
    : plans.filter((p) => p.status === AdminPlanStatus.ARCHIVED || p.status === AdminPlanStatus.DEPRECATED);

  return (
    <Box sx={{ p: 3 }}>
      <Title title="Forfaits" />

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Gestion des forfaits
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Créez et gérez vos forfaits d'abonnement
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Nouveau forfait
        </Button>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tabValue}
        onChange={(_, v) => setTabValue(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label={`Tous (${plans.length})`} />
        <Tab label={`Actifs (${plans.filter((p) => p.status === AdminPlanStatus.ACTIVE).length})`} />
        <Tab label={`Brouillons (${plans.filter((p) => p.status === AdminPlanStatus.DRAFT || p.status === AdminPlanStatus.REVIEW).length})`} />
        <Tab label={`Archivés (${plans.filter((p) => p.status === AdminPlanStatus.ARCHIVED || p.status === AdminPlanStatus.DEPRECATED).length})`} />
      </Tabs>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Erreur lors du chargement des forfaits: {error.message}
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Card>
                <CardContent>
                  <Skeleton variant="text" width="60%" height={32} />
                  <Skeleton variant="text" width="40%" />
                  <Skeleton variant="text" width="100%" height={60} sx={{ my: 2 }} />
                  <Skeleton variant="text" width="50%" height={40} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Plans Grid */}
      {!loading && filteredPlans.length === 0 && (
        <Alert severity="info">
          Aucun forfait trouvé. Créez votre premier forfait pour commencer.
        </Alert>
      )}

      {!loading && filteredPlans.length > 0 && (
        <Grid container spacing={3}>
          {filteredPlans.map((plan) => (
            <Grid item xs={12} sm={6} md={3} key={plan.id}>
              <PlanCard plan={plan} onEdit={handleEdit} onStatusChange={handleStatusChange} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create Dialog */}
      <CreatePlanDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreated={() => refetch()}
      />

      {/* Edit Dialog */}
      <EditPlanDialog
        open={editDialogOpen}
        plan={selectedPlan}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedPlan(null);
        }}
        onUpdated={() => refetch()}
      />
    </Box>
  );
}

export default PlanList;
